import Keycloak from 'keycloak-js';

const keycloak =
	process.env.REACT_APP_USE_KEYCLOAK === '1'
	? new Keycloak('appTrack/keycloak.json')
	: {
		init: function (options) {
			return new Promise(function (resolve, reject) {
				resolve(true);
			});
		}
	};

/**
 *  @brief Post initialize keycloak assets
 *  
 *  @details This function runs after keycloak has been initialized and it is
 *           authenticated, in order to initialize functions necessary for
 *           session tracking.
 */
keycloak.postInit = (params) => {
	keycloak.isAuthPages = (params && params.hasOwnProperty("isAuthPages") ? params.isAuthPages : null);
	keycloak.redirectToAlreadyLogged = (params && params.hasOwnProperty("redirectToAlreadyLogged") ? params.redirectToAlreadyLogged : () => {});
	keycloak.redirectToNewPassword = (params && params.hasOwnProperty("redirectToNewPassword") ? params.redirectToNewPassword : () => {});
	keycloak.appContextUrl = window.location.protocol + '//' + window.location.hostname +':'+process.env.REACT_APP_BACKEND_HOST+'/'+process.env.REACT_APP_FLOWABLE_CONTEXT_PATH;
	// isMultiDomain is true when we are working with the dev versions of the
	// frontend React apps, which means that each one is served in a different
	// port and consequently a different domain; in this case sharing of local
	// storage is done on the domain of the backend; the frontend apps can
	// read and store to the local storage of the backend through an iframe
	keycloak.isMultiDomain = /^http(s|):\/\/[^:]+:[0-9]+\/.*$/.test(keycloak.appContextUrl);
	// store whether an oauth2 provider is used to log users in parallel with
	// IDM login form
	keycloak.useOAuth2Provider = (process.env.REACT_APP_USE_OAUTH2_PROVIDER === '1');
	// store whether the user has logged through an OAuth2 provider; in this
	// case the endpoints change; initially it is null
	keycloak.userIsOAuth2 = null;

	/**
	 *  @brief Logout
	 *  
	 *  @details This function logs out the current user and redirects to login.
	 */
	 keycloak.logout = () => {
		window.location.href = keycloak.appContextUrl + keycloak.adjustEndpoint('/exit');
	}

	/**
	 *  @brief Redirect to Login
	 *  
	 *  @details This function logs out the current user and redirects to login.
	 *           If this is an auth page, then logging out is deactivated
	 *           because the user is already logged out.
	 * 
	 *           Furthermore, for external auth configuration, this function
	 *           will also notify all other open tabs that they need to
	 *           invalidate the user.
	 */
	keycloak.redirectToLogin = () => {
		if (process.env.REACT_APP_USE_KEYCLOAK === '2') {
			// notify other tabs that must invalidate the user
			keycloak.tracker.notifyMustInvalidate();
		}
		if (process.env.REACT_APP_USE_KEYCLOAK === '1' || process.env.REACT_APP_USE_KEYCLOAK === '2') {
			if (!keycloak.isAuthPages) {
				keycloak.logout();
			}
		}
	}

	/**
	 *  @brief Redirect to Home Page
	 *  
	 *  @details This function redirects to the home page.
	 */
	 keycloak.redirectToHome = () => {
		window.location.href = keycloak.appContextUrl + keycloak.adjustEndpoint('/start');
	}

	/**
	 *  @brief Convert Endpoint to OAuth2
	 *  
	 *  @return The converted endpoint.
	 *  
	 *  @details This function converts an endpoint to its oauth2 one.
	 */
	keycloak.toOAuth2 = (endpoint) => {
		if (endpoint.startsWith("/oauth2/")) return endpoint;
		else return "/oauth2" + endpoint;
	}

	/**
	 *  @brief Adjust Endpoint
	 * 
	 *  @return The adjusted endpoint.
	 *  
	 *  @details This function adjusts an endpoint taking into account if the
	 *           logged user is from oauth2.
	 */
	keycloak.adjustEndpoint = (endpoint) => {
		if (keycloak.userIsOAuth2 === true)
			endpoint = keycloak.toOAuth2(endpoint);
		return endpoint;
	}

	/**
	 *  @brief Execute Post Login Actions
	 *  
	 *  @param [object] data Post login data
	 *  @param [function] callbank A function to run after login actions have
	 *                    finished, in order to continue login flow.
	 *  
	 *  @details This function executes post login actions.
	 */
	keycloak.setPostLoginActions = (data, callback) => {
		keycloak.tracker.setPostLoginActions(data, callback);
	}

	/**
	 *  @brief Log Function
	 *  
	 *  @param [object] obj The object to log
	 *  
	 *  @details This function outputs obj to console.
	 */
	keycloak.log = (obj) => {
		//console.log(obj);
	}

	/**
	 *  @brief Generate Random String
	 *  
	 *  @param [integer] len The length of the random string.
	 *  @return The generated random string.
	 *  
	 *  @details This function generates a random string comprising of latin
	 *           letters (small and caps) and numbers.
	 */
	 keycloak.generateRandomString = (len) => {
		var result = "";
		var characters = 'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789';
		var charactersLength = characters.length;
		for ( var i = 0; i < len; i++ ) {
			result += characters.charAt(Math.floor(Math.random() * charactersLength));
		}
		return result;
	};

	/**
	 *  @brief Parse JSON String
	 *  
	 *  @param [string] str The JSON string.
	 *  @param [object] def The default value if parsing fails.
	 *  @return The parsed JSON.
	 *  
	 *  @details This function safely parses a JSON string.
	 */
	keycloak.jsonParse = (str, def) => {
		var obj = def;
		if (str === null) return obj;
		try {
			obj = JSON.parse(str);
		}
		catch(e) {}
		return obj;
	};


	/**
	 *  @brief Start Session Tracker
	 *  
	 *  @details This function initializes and start the session tracker. The
	 *           session tracker tracks the user session and access token and
	 *           keeps them alive if the user is active, or redirects to the
	 *           logout page if the user is idle for a long time.
	 */
	keycloak.startSessionTracker = () => {
		if (process.env.REACT_APP_USE_KEYCLOAK === '1') {
			// initialize tracker assets
			const now = (new Date).getTime() / 1000;
			const tracker = {
				// initialize tracker constants
				accessTokenIntv: 60,
				shortIntv: 10,
				// initialize tracker variables
				lastUserTime: now,
				lastTokenTime: now,
			};
			tracker.tokenIntv = tracker.accessTokenIntv - tracker.shortIntv;
			
			keycloak.tracker = tracker;

			/**
			 *  @brief Short Session Tracker
			 *  
			 *  @details This function runs in short intervals and checks or
			 *           updates the access token. If the user is idle for a
			 *           long time, then it will stop running (otherwise the
			 *           session would saty alive forever and would never
			 *           expire).
			 */
			keycloak.tracker.shortSessionChecker = () => {
				const tracker = keycloak.tracker;
				const now = (new Date).getTime() / 1000;
				const idleTime = now - tracker.lastUserTime;
				const tokenTime = now - tracker.lastTokenTime;
				keycloak.log({f: "shortSessionChecker", idleTime: parseInt(idleTime), tokenTime: parseInt(tokenTime)});
				if (idleTime <= tracker.tokenIntv) {
					if (tokenTime >= tracker.tokenIntv) {
						tracker.updateAccessToken();
					}
					else {
						tracker.checkAccessToken();
					}
				}
				else {
					clearInterval(tracker.shortSessionCheckerIntv);
					tracker.shortSessionCheckerIntv = null;
				}
			}

			/**
			 *  @brief Update Access Token
			 *  
			 *  @details This function updates the access token if it is close
			 *           to its expiration. In case of error, this means that
			 *           the session is expired and the user is redirected to
			 *           login.
			 */
			keycloak.tracker.updateAccessToken = () => {
				keycloak.log({f: "updateAccessToken"});
				const tracker = keycloak.tracker;
				keycloak.updateToken(tracker.shortIntv)
				.then(function(refreshed) {
					if (refreshed) {
						tracker.lastTokenTime = (new Date).getTime() / 1000;
					}
				})
				.catch(function(e) {
					keycloak.log({f: "updateAccessToken error"});
					keycloak.redirectToLogin();
				});
			}

			/**
			 *  @brief Check Access Token
			 *  
			 *  @details This function checks whether the access token is valid
			 *           or not. If it is not, in will update it.
			 */
			keycloak.tracker.checkAccessToken = () => {
				keycloak.log({f: "checkAccessToken"});
				const tracker = keycloak.tracker;
				if (keycloak.isTokenExpired()) {
					tracker.updateAccessToken();
				}
			}

			/**
			 *  @brief User Acted Callback
			 *  
			 *  @details This function runs whenever the user interacts with the
			 *           page. This is a mechanism of detecting when the user is
			 *           idle. If the user has been idle for a long time, the
			 *           function will try to update the access token. If it
			 *           fails (meaning that the session is expired) it will
			 *           redirect to the login page.
			 */
			keycloak.tracker.userActed = () => {
				const tracker = keycloak.tracker;
				const now = (new Date).getTime() / 1000;
				const idleTime = now - tracker.lastUserTime;
				tracker.lastUserTime = now;
				if (idleTime > tracker.tokenIntv) {
					tracker.updateAccessToken();
				}
				if (tracker.shortSessionCheckerIntv == null) {
					tracker.shortSessionCheckerIntv = setInterval(tracker.shortSessionChecker, tracker.shortIntv * 1000);
				}
			}
			
			// initialize tracker timer
			tracker.shortSessionCheckerIntv = setInterval(tracker.shortSessionChecker, tracker.shortIntv * 1000);
			// initialize user action events
			document.addEventListener('mousedown', keycloak.tracker.userActed);
			document.addEventListener('mousemove', keycloak.tracker.userActed);
			document.addEventListener('touchstart', keycloak.tracker.userActed);
			document.addEventListener('scroll', keycloak.tracker.userActed);
			document.addEventListener('keydown', keycloak.tracker.userActed);
			window.addEventListener('focus', keycloak.tracker.userActed);
		}
		else if (process.env.REACT_APP_USE_KEYCLOAK === '2') {
			var now = (new Date()).getTime();
			const tracker = {
				// initialize session constants
				sessionMaxAge: 2678400,
				inactivityPeriod: 3600,
				shortIntv: 1,
				keepAliveBackTime: 10,
				dataBank: {},
				sessionData: {},
				// initialize tracker variables
				currentUserId: null,
				lastUserTimeMS: null,
				lastCheckTimeMS: null,
				lastInvalidateTimeMS: now,
				monitorPage: false,
				ifr: null,
			};

			keycloak.tracker = tracker;

			/**
			 *  @brief Install Monitor if IDM Session
			 *  
			 *  @param [object] data Data coming from the backend containing
			 *                  session time limits.
			 *  @param [boolean] monitorPage Set whether session listeners
			 *                   and trackers will be activated.
			 *  
			 *  @details This function initializes the monitoring of IDM session
			 *           (the session when there is no keycloak). Various
			 *           variables, required to keep track of the session, are
			 *           kept and shared across browser tabs through local
			 *           storage. This way tabs can notify each other on session
			 *           status changes.
			 * 
			 *           It is noted that the frontend uses different domains
			 *           than the backend. In order to be able to share the
			 *           local storage between the domains, the frontend loads
			 *           an iframe with the domain of the backend and exchanges
			 *           data with the iframe through postMessage(). The local
			 *           storage of the backend domain in used for sharing.
			 * 
			 *           This method can be called multiple times during the
			 *           life of a page, in order to activate or deactivate
			 *           the monitoring dynamically. This is useful when
			 *           redirecting to React pages without reloading.
			 */
			keycloak.tracker.installMonitorIdmSession = (data, monitorPage) => {
				// update session limits with the values set in backend props
				if (data) {
					if (data.hasOwnProperty("sessionMaxAge")) keycloak.tracker.sessionMaxAge = data.sessionMaxAge;
					if (data.hasOwnProperty("inactivityPeriod")) keycloak.tracker.inactivityPeriod = data.inactivityPeriod;
					if (data.hasOwnProperty("userId")) keycloak.tracker.currentUserId = data.userId;
				}
				const prevMonitorPage = keycloak.tracker.monitorPage;
				const eventsInitialized = (keycloak.tracker.ifr != null);
				keycloak.tracker.monitorPage = monitorPage;
				// if isMultiDomain is true then we need to use an iframe in
				// order to be able to store to the local storage of the backend
				if (keycloak.tracker.isMultiDomain) {
					// create the iframe with the domain of the backend only if
					// it hasn't already been created
					if (!eventsInitialized) {
						var ifr = document.createElement('IFRAME');
						ifr.src = keycloak.appContextUrl + '/gateway_rb2s72ps';
						ifr.style.display = 'none';
						ifr.addEventListener('load', function (e) {
							// initialize various listeners
							keycloak.tracker.initializeEvents();
							// start the sequence of session tracking
							keycloak.tracker.scheduleNextShortIdmSessionChecker();
						});
						document.body.appendChild(ifr);
						keycloak.tracker.ifr = ifr;
					}
				}
				else {
					// initialize local storage data
					keycloak.tracker.sessionData.initialize();
					// initialize various listeners
					keycloak.tracker.initializeEvents();
					// start the sequence of session tracking
					keycloak.tracker.scheduleNextShortIdmSessionChecker();
					// we set ifr to 1 to denote that initialization of events
					// has been done
					keycloak.tracker.ifr = 1;
				}
				// we want to initiate session trackers only if they have
				// previously stopped
				if (eventsInitialized && monitorPage && !prevMonitorPage) {
					keycloak.tracker.scheduleNextShortIdmSessionChecker();
				}
			}

			/**
			 *  @brief Execute Post Login Actions
			 *  
			 *  @param [object] data Post login data.
			 *  @param [function] callback A callback function to execute after
			 *                    post login actions finish.
			 *  
			 *  @details This function is used to store rememberMe state in the
			 *           local storage, after the user logins. This is necessary
			 *           so that the session tracker does not logout the user
			 *           if inactivity period has passed when rememberMe is
			 *           activated.
			 */
			 keycloak.tracker.setPostLoginActions = (data, callback) => {
				// if isMultiDomain is true then we need to set rememberMe flag
				// to the local storage of the backend through an iframe
				if (keycloak.tracker.isMultiDomain) {
					// function container for the post login actions
					const callBackContainer = (data, callback) => {
						const bankId = keycloak.tracker.newBankData({
							data: data,
							callback: callback,
						});
						keycloak.tracker.setRememberUser(data.rememberUser, bankId);
					};
					if (keycloak.tracker.ifr == null) {
						var ifr = document.createElement('IFRAME');
						ifr.src = keycloak.appContextUrl + '/gateway_rb2s72ps';
						ifr.style.display = 'none';
						ifr.addEventListener('load', function (e) {
							keycloak.tracker.monitorPage = true;
							keycloak.tracker.initializeEvents();
							callBackContainer(data, callback);
						});
						document.body.appendChild(ifr);
						keycloak.tracker.ifr = ifr;
					}
					else {
						callBackContainer(data, callback);
					}
				}
				else {
					keycloak.tracker.setRememberUser(data.rememberUser);
					callback(data);
				}
			}

			/**
			 *  @brief Add New Bank Data
			 *  
			 *  @param [object] data Bank data.
			 *  @return The new bank ID.
			 *  
			 *  @details This function stores new data in tracker data bank and
			 *           returns the associated unique ID, so that this data
			 *           can then be retrived based on this ID.
			 */
			 keycloak.tracker.newBankData = (data) => {
				const newBankId = "id_" + keycloak.generateRandomString(8);
				keycloak.tracker.dataBank[newBankId] = data;
				return newBankId;
			}

			/**
			 *  @brief Get Bank Data
			 *  
			 *  @param [string] bankId The bank ID.
			 *  @return The associated bank data.
			 *  
			 *  @details This function retrieves data from tracker data bank
			 *           based on their associated ID.
			 */
			 keycloak.tracker.getBankData = (bankId) => {
				var data = null;
				if (bankId != "" && keycloak.tracker.dataBank.hasOwnProperty(bankId)) {
					data = keycloak.tracker.dataBank[bankId];
				}
				return data;
			}

			/**
			 *  @brief Initialize IDM Session Tracking Listeners
			 *  
			 *  @details This function initializes the listeners required for
			 *           monitoring the IDM session. Three types of listeners
			 *           are defined. The first ones track whenever the user
			 *           interacts with the page and they are used to identify
			 *           the idle time. The second one processes messages coming
			 *           from the iframe. The third one intercepts axios
			 *           responses.
			 */
			 keycloak.tracker.initializeEvents = () => {
				document.addEventListener('mousedown', keycloak.tracker.userActed);
				document.addEventListener('mousemove', keycloak.tracker.userActed);
				document.addEventListener('touchstart', keycloak.tracker.userActed);
				document.addEventListener('scroll', keycloak.tracker.userActed);
				document.addEventListener('keydown', keycloak.tracker.userActed);
				window.addEventListener('focus', keycloak.tracker.userActed);
				// if isMultiDomain is true then we set a window onmessage
				// listener to receive messages from the iframe that points to
				// the backend domain
				if (keycloak.isMultiDomain) {
					window.addEventListener("message", keycloak.tracker.receiveIdmSessionData, false);
				}

				if (keycloak.hasOwnProperty("axiosInstance") && keycloak.axiosInstance != null) {
					keycloak.tracker.installAxiosInterceptor(keycloak.axiosInstance);
				}
			}

			/**
			 *  @brief Axios Responses Interceptor
			 *  
			 *  @param [object] instance The axios instance to intercept.
			 * 
			 *  @details This function intercepts axios responses. In case of an
			 *           error in a response it will invalidate the user. This
			 *           way we will avoid cases where the session has expired
			 *           without the frontend knowing.
			 */
			 keycloak.tracker.installAxiosInterceptor = (instance) => {
				instance.interceptors.request.use(function (config) {
					config.url = keycloak.adjustEndpoint(config.url);
					return config;
				}, function (error) {
					return Promise.reject(error);
				});
				instance.interceptors.response.use((response) => {
					return response;
				}, (error) => {
					if (keycloak.tracker.monitorPage) {
						keycloak.tracker.invalidateUser((data) => keycloak.tracker.checkInvalidatedUser(data));
					}
					return Promise.reject(error);
				});
			}

			/**
			 *  @brief Schedule Next IDM Session Checker
			 *  
			 *  @details This function schedules the next time the IDM session
			 *           checker function will run. When the check finishes this
			 *           function will be called again. This way the checker
			 *           function will run repeatedly as long as the page is
			 *           not refreshed.
			 */
			 keycloak.tracker.scheduleNextShortIdmSessionChecker = () => {
				setTimeout(keycloak.tracker.shortIdmSessionCheckerTrigger, keycloak.tracker.shortIntv * 1000);
			}

			/**
			 *  @brief User Acted
			 *  
			 *  @details This function runs whenever the user interacts with the
			 *           page.
			 */
			 keycloak.tracker.userActed = () => {
				keycloak.tracker.lastUserTimeMS = (new Date()).getTime();
			}

			/**
			 *  @brief Send A Message to the Iframe
			 *  
			 *  @param [string] message The message to send to the iframe.
			 * 
			 *  @details This function sends a message to the iframe. This way
			 *           it can interact with the iframe and read or update the
			 *           session variables kept in the local storage.
			 */
			 keycloak.tracker.sendMessageToIframe = (message) => {
				if (keycloak.tracker.ifr) {
					keycloak.tracker.ifr.contentWindow.postMessage(message, "*");
				}
			}

			/**
			 *  @brief Request Session Data from Local Storage
			 *  
			 *  @details This function asks from the iframe to send the session
			 *           variables kept in the local storage. This operation
			 *           will also trigger the session checker function when the
			 *           iframe responds.
			 */
			 keycloak.tracker.shortIdmSessionCheckerTrigger = () => {
				if (keycloak.isMultiDomain) {
					keycloak.tracker.sendMessageToIframe("getSessionData");
				}
				else {
					const data = keycloak.tracker.sessionData.getObject(null);
					if (data != null) keycloak.tracker.processIdmSessionData(data);
					keycloak.tracker.scheduleNextShortIdmSessionChecker();
				}
			}
		
			/**
			 *  @brief Notify Local Storage That User Interacted
			 *  
			 *  @param [integer] userTime The time the user interacted.
			 * 
			 *  @details This function notifies the local storage that the user
			 *           interacted with the page. This way the local storage
			 *           can track the idle time for all the open browser tabs.
			 */
			 keycloak.tracker.notifyUserActed = (userTime) => {
				if (keycloak.tracker.isMultiDomain) keycloak.tracker.sendMessageToIframe("userActed:" + userTime);
				else keycloak.tracker.sessionData.userActed(userTime);
			}
		
			/**
			 *  @brief Notify Local Storage That User Must Be Invalidated
			 *  
			 *  @details This function notifies the local storage (and
			 *           consequently all other tabs) that the user must be
			 *           invalidated. It is used when the user logs out in one
			 *           tab, so that the other tabs are notified and update
			 *           their session status. 
			 */
			 keycloak.tracker.notifyMustInvalidate = () => {
				if (keycloak.tracker.isMultiDomain) keycloak.tracker.sendMessageToIframe("mustInvalidate");
				else keycloak.tracker.sessionData.mustInvalidate();
			}
		
			/**
			 *  @brief Notify Local Storage That Session Kept Alive
			 *  
			 *  @details This function notifies the local storage that the
			 *           session has just been kept alive.
			 * 
			 *           The session is kept alive by intentionally invalidating
			 *           the user before reaching the inactivity period limit.
			 *           This is necessary, otherwise the backend might logout
			 *           the user if the inactivity period has passed (note that
			 *           if the user interacts with the page but no endpoint is 
			 *           called, the backend does not know it).
			 * 
			 *           The local storage keeps track of when the session is
			 *           kept alive, so that invalidation is done only when it
			 *           is necessary.
			 */
			 keycloak.tracker.notifyKeptAlive = () => {
				if (keycloak.tracker.isMultiDomain) keycloak.tracker.sendMessageToIframe("updateKeepAlive");
				else keycloak.tracker.sessionData.updateKeepAlive();
			}
		
			/**
			 *  @brief Notify Local Storage That User Must Be Remembered
			 *  
			 *  @param [boolean] rememberUser Flag whether to remember user or
			 *                   not.
			 *  @param [string] bankId The bank ID that keeps post login data.
			 *  
			 *  @details This function notifies the local storage that the
			 *           user must be remembered.
			 */
			 keycloak.tracker.setRememberUser = (rememberUser, bankId) => {
				if (keycloak.tracker.isMultiDomain) {
					if (typeof bankId == "undefined" || bankId == null) bankId = "";
					if (rememberUser) keycloak.tracker.sendMessageToIframe("setRememberMe:" + bankId);
					else keycloak.tracker.sendMessageToIframe("setNotRememberMe:" + bankId);
				}
				else keycloak.tracker.sessionData.setRememberMe(rememberUser);
			}

			/**
			 *  @brief Receive IDM Session Data From Iframe
			 *  
			 *  @param [object] event The onmessage event object.
			 * 
			 *  @details This function is the onmessage event handler when the
			 *           iframe sends the session data to the page. It processes
			 *           the data and then it schedules the IDM session checker
			 *           again.
			 * 
			 *           If the message starts with setRememberMeRet it gets
			 *           post login data from tracker data bank and continues
			 *           the login flow by calling the post login callback.
			 */
			 keycloak.tracker.receiveIdmSessionData = (event) => {
				if (!keycloak.tracker.monitorPage) return;
				if (typeof event.data == "string" && event.data.startsWith("idmSessionData:")) {
					var data = null;
					try {
						data = JSON.parse(event.data.substr(15));
					}
					catch (e) {}
					if (data != null) keycloak.tracker.processIdmSessionData(data);
					keycloak.tracker.scheduleNextShortIdmSessionChecker();
				}
				else if (typeof event.data == "string" && event.data.startsWith("setRememberMeRet:")) {
					const bankId = event.data.substr(17);
					const bankData = keycloak.tracker.getBankData(bankId);
					if (bankData != null) {
						bankData.callback(bankData.data);
					}
				}
			}

			/**
			 *  @brief IDM Session Data Checker
			 *  
			 *  @param [object] data The local storage session data.
			 * 
			 *  @details This function is the actual checker of the IDM session
			 *           data stored in the local storage. Depending on the
			 *           session status it may logout the user, or invalidate
			 *           the session or just update the local storage.
			 */
			 keycloak.tracker.processIdmSessionData = (data) => {
				var now = (new Date()).getTime();
				// check if user has acted in order to notify the iframe
				if (keycloak.tracker.lastUserTimeMS != null && (keycloak.tracker.lastCheckTimeMS == null || keycloak.tracker.lastUserTimeMS > keycloak.tracker.lastCheckTimeMS)) {
					keycloak.tracker.lastCheckTimeMS = keycloak.tracker.lastUserTimeMS;
					keycloak.tracker.notifyUserActed(keycloak.tracker.lastUserTimeMS);
				}
				// check if user must be invalidated, resulting in user logout
				// on error or in redirection to home page on user change
				if (data.lastMustInvalidateTimeMS != null && data.lastMustInvalidateTimeMS > keycloak.tracker.lastInvalidateTimeMS) {
					keycloak.tracker.lastInvalidateTimeMS = data.lastMustInvalidateTimeMS;
					keycloak.tracker.invalidateUser((data) => keycloak.tracker.checkInvalidatedUser(data));
				}
				// check if user must be invalidated to keep session alive
				if (now - data.lastKeepAliveTimeMS > (keycloak.tracker.inactivityPeriod - keycloak.tracker.keepAliveBackTime) * 1000) {
					keycloak.tracker.notifyKeptAlive();
					keycloak.tracker.invalidateUser((data) => keycloak.tracker.checkInvalidatedUser(data));
				}
				// check if idle time has reached inactivity period limit
				// only when rememberMe option has not been selected
				if (!data.rememberMe && data.idleStartTimeMS != null && now - data.idleStartTimeMS > keycloak.tracker.inactivityPeriod * 1000) {
					keycloak.redirectToLogin();
				}
			}


			/**
			 *  @brief Invalidate IDM Session
			 *  
			 *  @param [function] successCallback The function to run on
			 *                             invalidation success.
			 *  @param [function] failCallback The function to run on
			 *                             invalidation failure.
			 *  @param [function] mustChangePasswordCallback The function to run
			 *                             invalidation success where the user
			 *                             must change password.
			 * 
			 *  @details This function invalidates the IDM session by calling
			 *           /account-checker endpoint. If the user is still valid
			 *           the session token will be refreshed (to reset
			 *           inactivity counter) and successCallback function will
			 *           be executed. If the user is not valid then failCallback
			 *           will be executed. In case the user must change their
			 *           password then mustChangePasswordCallback will be
			 *           executed.
			 * 
			 *           It is noted that this function first calls
			 *           /account-checker in order to determine if an IDM user
			 *           is logged. If not, it will then call /account-checker
			 *           again (with oauth2 prefix in URL) in order to
			 *           determine if an oauth2 user is logged.
			 */
			 keycloak.tracker.invalidateUser = (successCallback, failCallback, mustChangePasswordCallback) => {
				// set default handler for failCallback if not defined
				if (typeof failCallback == "undefined") {
					failCallback = (error) => {
						keycloak.tracker.catchError(error);
					};
				}
				// set default handler for mustChangePasswordCallback if not
				// defined; the default action is to log out the user
				if (typeof mustChangePasswordCallback == "undefined") {
					mustChangePasswordCallback = (data) => {
						keycloak.logout();
					};
				}
				const ctx = keycloak.appContextUrl.replace(/^https?:\/\/[^\/]+/, "");
				const checkerEndpoint = '/app/rest/account-checker';
				// checkerFunc is the function that does the actual check
				const checkerFunc = (endpoint, onErrorFunc, isOAuth2) => {
					fetch(window.location.origin + ctx + endpoint)
					.then(res => {
						if (res && res.ok) {
							return res.json();
						} else {
							onErrorFunc(res);
						}
					})
					.then(data => {
						if (data && data.ok) {
							keycloak.userIsOAuth2 = isOAuth2;
							if (data.mustChangePassword) {
								mustChangePasswordCallback(data);
							}
							else {
								successCallback(data);
							}
						} else {
							onErrorFunc(null);
						}
					})
					.catch(onErrorFunc);
				};
				// onErrorFunc retries checkerFunc for the oauth2 endpoint if
				// an IDM user is not logged
				const onErrorFunc = (error) => {
					// check oauth2 user if oauth provider is used
					if (keycloak.useOAuth2Provider) {
						checkerFunc(keycloak.toOAuth2(checkerEndpoint), failCallback, true);
					}
					else {
						failCallback(error);
					}
				};
				checkerFunc(checkerEndpoint, onErrorFunc, false);
			}

			/**
			 *  @brief Invalidate IDM Session for Auth Pages
			 *  
			 *  @param [boolean] proceedIfNoUser Set whether the page requires
			 *                   the user to be logged or not.
			 *  @param [function] proceedCallback Function to run on proceed.
			 *  @param [function] mustChangePasswordCallback Function to run 
			 *                    on user who must change password.
			 * 
			 *  @details This function checks if a user is logged by
			 *           invalidating the IDM session. On success, it will
			 *           proceed depending whether the page requires the user
			 *           to be logged or not.
			 */
			 keycloak.tracker.invalidateUserForAuthPages = (proceedIfNoUser, proceedCallback, mustChangePasswordCallback) => {
				keycloak.isAuthPages = proceedIfNoUser;
				if (!proceedIfNoUser) {
					keycloak.tracker.invalidateUser((data) => {
						proceedCallback();
					}, undefined, mustChangePasswordCallback);	
				}
				else {
					keycloak.tracker.invalidateUser(() => {
						keycloak.redirectToAlreadyLogged();	
					}, proceedCallback, mustChangePasswordCallback);
				}
			}

			/**
			 *  @brief Check Invalidated User
			 *  
			 *  @param [object] data The data received after invalidation.
			 * 
			 *  @details This function checks whether the user received after
			 *           invalidation is the same with the one on the page. If
			 *           not, then it will redirect to home page.
			 */
			 keycloak.tracker.checkInvalidatedUser = (data) => {
				if (keycloak.tracker.currentUserId != null && keycloak.tracker.currentUserId != data.userId) {
					keycloak.redirectToHome();
				}
			}

			/**
			 *  @brief Handle Tracker Error
			 *  
			 *  @param [object] error The error data.
			 * 
			 *  @details This function handles errors when it is attempted to
			 *           invalidate the IDM session. It will redirect the user
			 *           to login if the user is invalid.
			 */
			 keycloak.tracker.catchError = (error) => {
				// console.log("Check account error status: ", error);
				if (!error || error.status === 401 || error.message.toLowerCase().includes("failed to fetch")) {
					keycloak.redirectToLogin();
				}
			}

			/**
			 *  @brief Get Session Data
			 *  
			 *  @return The session data as a JSON string.
			 * 
			 *  @details This function reads any stored session data from
			 *           local storage property sessionData. The session data
			 *           are stored as a JSON string.
			 */
			keycloak.tracker.sessionData.get = () => {
				return window.localStorage.getItem("sessionData");
			}

			/**
			 *  @brief Get Session Data Object
			 *  
			 *  @param [object] def Default value for the session data object.
			 *  @return The session data as a parsed JSON object.
			 * 
			 *  @details This function reads any stored session data from
			 *           local storage property sessionData as an object.
			 */
			keycloak.tracker.sessionData.getObject = (def) => {
				return keycloak.jsonParse(keycloak.tracker.sessionData.get(), def);
			}

			/**
			 *  @brief Update Session Data Object
			 *  
			 *  @param [object] data The updated session data object.
			 * 
			 *  @details This function updates the stored session data in the
			 *           local storage.
			 */
			keycloak.tracker.sessionData.update = (data) => {
				window.localStorage.setItem("sessionData", JSON.stringify(data));
			}

			/**
			 *  @brief Initialize Session Data
			 *  
			 *  @details This function initializes the stored session data in
			 *           the local storage.
			 */
			keycloak.tracker.sessionData.initialize = () => {
				var data = keycloak.tracker.sessionData.getObject({});
				if (!data.hasOwnProperty("idleStartTimeMS")) data.idleStartTimeMS = null;
				if (!data.hasOwnProperty("lastMustInvalidateTimeMS")) data.lastMustInvalidateTimeMS = null;
				if (!data.hasOwnProperty("lastKeepAliveTimeMS")) data.lastKeepAliveTimeMS = null;
				if (!data.hasOwnProperty("rememberMe")) data.rememberMe = false;
				var now = (new Date()).getTime();
				data.idleStartTimeMS = now;
				data.lastKeepAliveTimeMS = now;
				keycloak.tracker.sessionData.update(data);		
			}

			/**
			 *  @brief Update Session Data When User Acted
			 *  
			 *  @param [integer] userTimeMS The timestamp of the time the user
			 *                   acted.
			 * 
			 *  @details This function updates the stored session data to keep
			 *           the last time the user interacted with the page.
			 */
			keycloak.tracker.sessionData.userActed = (userTimeMS) => {
				var data = keycloak.tracker.sessionData.getObject(null);
				if (data && (data.idleStartTimeMS == null || userTimeMS > data.idleStartTimeMS)) {
					data.idleStartTimeMS = userTimeMS;
					keycloak.tracker.sessionData.update(data);
				}
			}
		
			/**
			 *  @brief Update Session Data When Must Invalidate
			 * 
			 *  @details This function updates the stored session data to set
			 *           that all pages must invalidate the user.
			 */
			keycloak.tracker.sessionData.mustInvalidate = () => {
				var data = keycloak.tracker.sessionData.getObject(null);
				if (data) {
					data.lastMustInvalidateTimeMS = (new Date()).getTime();
					keycloak.tracker.sessionData.update(data);
				}
			}

			/**
			 *  @brief Update Session Data Keep Alive
			 * 
			 *  @details This function updates the stored session data to reset
			 *           the last keep alive time.
			 */
			keycloak.tracker.sessionData.updateKeepAlive = () => {
				var data = keycloak.tracker.sessionData.getObject(null);
				if (data) {
					data.lastKeepAliveTimeMS = (new Date()).getTime();
					keycloak.tracker.sessionData.update(data);
				}
			}

			/**
			 *  @brief Update Session Data RememberMe
			 * 
			 * @param [boolean] activate Whether to activate rememberMe.
			 * 
			 *  @details This function updates the stored session data
			 *           rememberMe flag.
			 */
			keycloak.tracker.sessionData.setRememberMe = (activate) => {
				var data = keycloak.tracker.sessionData.getObject(null);
				if (data) {
					data.rememberMe = activate;
					keycloak.tracker.sessionData.update(data);
				}
			}
			

		}
	}

	keycloak.startSessionTracker();
}

/**
 *  @brief Load React DOM
 *  
 *  @param [function] callback The callback function that load the DOM.
 *  
 *  @details This function loads the React DOM. Before loading of React DOM, the
 *           function triggers a specific endpoint of the back-end application
 *           to make sure that the back-end syncs the user. This trigger happens
 *           inside an iframe.
 */
keycloak.loadReactDom = (callback) => {
	if (process.env.REACT_APP_USE_KEYCLOAK === '1') {
		// first we need to reload flowable root page so that we make sure that
		// flowable has correctly read the user, otherwise the ui endpoints will not
		// work; we do this by loading flowable root page inside a hidden iframe
		var ifr = document.createElement('IFRAME');
		ifr.src = keycloak.appContextUrl + '/gateway_jftezm9m';
		ifr.style.display = 'none';
		ifr.addEventListener('load', function (e) {
			callback();
		});
		document.body.appendChild(ifr);
	} else if (process.env.REACT_APP_USE_KEYCLOAK === '2') {
		if (keycloak.isAuthPages === null) {
			keycloak.tracker.invalidateUser((data) => {
				keycloak.tracker.installMonitorIdmSession(data, true);
				callback();
			});
		}
		else {
			// in case of Auth Pages then DOM is loaded and session control is
			// handled by useEffect() of each page.
			callback();
		}
	} else {
		callback();
	}
};

/**
 *  @brief Install Axios Interceptor
 *  
 *  @details This function installs the special axios interceptor.
 */
keycloak.installAxiosInterceptor = (instance) => {
	keycloak.axiosInstance = instance;
}

export default keycloak;